import React, { useState, useEffect, useCallback } from 'react';
import { searchUsers } from '../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { UserCheck, Search } from 'lucide-react';

/**
 * <AssignUserDialog open onSelect={(user) => ...} onClose={() => ...} />
 * Reusable user picker. Searches /cert-assign/users/search and lets admin pick one.
 */
const AssignUserDialog = ({ open, onClose, onSelect, currentUserId }) => {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchUsers = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await searchUsers(query);
      setUsers(r.data || []);
    } catch {
      setUsers([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (open) fetchUsers('');
  }, [open, fetchUsers]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchUsers(q), 300);
    return () => clearTimeout(t);
  }, [q, open, fetchUsers]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col" data-testid="assign-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-primary" />
            Asignar a usuario registrado
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o email..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="user-search-input"
            />
          </div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
            {loading ? (
              <p className="text-sm text-muted-foreground text-center py-4">Buscando...</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Sin resultados</p>
            ) : (
              users.map(u => (
                <button
                  key={u.id}
                  onClick={() => { onSelect(u); onClose(); }}
                  className={`w-full text-left p-3 border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors ${currentUserId === u.id ? 'border-primary bg-primary/5' : ''}`}
                  data-testid={`user-option-${u.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{u.name || u.email}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">{u.role}</Badge>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignUserDialog;
